import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useState,
  useMemo,
} from 'react'
import { LINKER_MODEL_IDS } from 'src/lib/Linkers/Linkers.constants'
import { LinkerError } from 'src/lib/Linkers/Linkers.components'
import { FileSelector } from 'src/components/Reusable/Forms/FileSelector/FileSelector'
import { stripFileExtension } from 'src/components/Reusable/Forms/FileSelector/FileSelector.helpers'
import {
  useHasReceptor,
  useReceptor,
} from 'src/pages/Receptors/receptors.hooks'
import { TextButton } from '../../Reusable/Buttons/TextButton'
import { DocumentFormData, DocumentType } from './AddDocumentForm'
import {
  PolicyDocGoogleDriveButton,
  PolicyDocLinkTypeButton,
  PolicyDocSharepointButton,
} from './AddDocumentForm.components'

type DocumentTypeSelectorProps = {
  setFormData: Dispatch<SetStateAction<DocumentFormData>>
  type: DocumentType
  setType: Dispatch<SetStateAction<DocumentType>>
  hide: () => void
  policyId: string
}

export const DocumentTypeSelector = ({
  setFormData,
  type,
  setType,
  hide,
  policyId,
}: DocumentTypeSelectorProps): JSX.Element => {
  const [gdriveError, setGdriveError] = useState<string>('')

  const { data: HasSharepointReceptor } = useHasReceptor('trr-sharepoint', true)
  const { data: HasGdriveReceptor } = useHasReceptor('trr-gdrive', true)
  const hasSharepointReceptor = useMemo(
    () => HasSharepointReceptor?.getHasReceptor() || false,
    [HasSharepointReceptor],
  )
  const hasGdriveReceptor = useMemo(
    () => HasGdriveReceptor?.getHasReceptor() || false,
    [HasGdriveReceptor],
  )
  const { data: spReceptor } = useReceptor({
    modelId: 'trr-sharepoint',
    shouldFetch: hasSharepointReceptor,
  })
  const { data: gdriveReceptor } = useReceptor({
    modelId: 'trr-gdrive',
    shouldFetch: hasGdriveReceptor,
  })
  const isSharepointConnected = useMemo(() => {
    if (!spReceptor) return false
    return spReceptor.getIsenabled()
  }, [spReceptor])
  const isGdriveConnected = useMemo(() => {
    if (!gdriveReceptor) return false
    return gdriveReceptor.getIsenabled()
  }, [gdriveReceptor])

  const clearText = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault()
      setFormData((state) => {
        const newState: DocumentFormData = {
          ...state,
          mime: '',
          body: '',
          caption: '',
        }
        return newState
      })
      if (type !== DocumentType.LINK) setType(DocumentType.NULL)
    },
    [setFormData, type, setType],
  )

  if (type !== DocumentType.NULL) {
    return <TextButton onClick={clearText}>Clear</TextButton>
  }

  const handleFileChange = (files: File[]) => {
    if (files.length === 0) return
    const file = files[0]
    const fileType = file.type

    setFormData((state) => {
      const newState: DocumentFormData = {
        ...state,
        mime: fileType,
        body: file,
        caption: stripFileExtension(file.name),
      }
      return newState
    })
    setType(DocumentType.FILE)
  }

  let alternateTypeOptions = [
    <PolicyDocLinkTypeButton
      setFormData={setFormData}
      setType={setType}
      key={'link'}
    />,
  ]

  if (isGdriveConnected) {
    alternateTypeOptions = [
      ...alternateTypeOptions,
      <PolicyDocGoogleDriveButton
        policyId={policyId}
        hide={hide}
        setGdriveError={setGdriveError}
        key={'gdrive-connected'}
      />,
    ]
  }

  if (isSharepointConnected) {
    alternateTypeOptions = [
      ...alternateTypeOptions,
      <PolicyDocSharepointButton
        key={'sharepoint'}
        setFormData={setFormData}
        setType={setType}
      />,
    ]
  }

  return (
    <>
      <FileSelector
        alternateTypeOptions={alternateTypeOptions}
        onChange={handleFileChange}
        isLoading={false}
        isValid={true}
        errorMessage={''}
      />
      {gdriveError && (
        <LinkerError
          errMsg={gdriveError}
          linkerId={LINKER_MODEL_IDS.GOOGLE_DRIVE}
        />
      )}
    </>
  )
}
